import {AutoLoadedTerms, ForceLoadedTerms, ForceLoadedTermsRequest, LoadedTerms, Term} from "~/types/term-type.ts";
import {
    ActionCreatorWithoutPayload,
    ActionCreatorWithPayload,
    createAction,
    createSlice,
    Draft,
    PayloadAction
} from "@reduxjs/toolkit";

export interface TermState {
    active: Term[],
    inactive: Term[],
    new: Term[],
    links: {
        forceImport: string,
        load: string,
        update: string,
    },
}

export const initialState: TermState = {
    active: [],
    inactive: [],
    new: [],
    links: {
        forceImport: '',
        load: '',
        update: '',
    },
};

const slice = createSlice({
        name: 'terms',
        initialState: initialState,
        reducers: {
            receiveTerms: (draft: Draft<TermState>, {payload}: PayloadAction<LoadedTerms>) => {
                const terms: Term[] = payload._embedded.termList;

                draft.active = terms.filter((term: Term) => term.enabled === true);
                draft.inactive = terms.filter((term: Term) => term.enabled === false);
                draft.new = terms.filter((term: Term) => term.enabled !== true && term.enabled !== false);
            },
            receiveAutoLoadedTerms: (draft: Draft<TermState>, {payload}: PayloadAction<LoadedTerms>) => {
                draft.links.forceImport = '';
                draft.links.load = '';
                draft.links.update = '';
            },
            receiveForcedLoadedTerms: (draft: Draft<TermState>, {payload}: PayloadAction<ForceLoadedTerms>) => {
                const forcedLoadedTerms: Term[] = payload.loadedTerms;
                const newLoadedTerms: Term[] = forcedLoadedTerms.filter((term: Term) => term.enabled != true && term.enabled != false);

                // New terms returned from the force load terms call.
                // So add them to the list of terms that need to be addressed.
                draft.new = [...draft.new, ...newLoadedTerms];
            },
            receiveUpdatedTerms: (draft: Draft<TermState>, {payload}: PayloadAction<LoadedTerms>) => {
                const updatedTerms: Term[] = payload._embedded.termList;

                draft.active = draft.active.filter(term => {
                    const indexActive = updatedTerms.find(term1 => term.id === term1.id);
                    return indexActive === undefined;
                })

                draft.inactive = draft.inactive.filter(term => {
                    const indexInactive = updatedTerms.find(term1 => term.id === term1.id);
                    return indexInactive === undefined;
                });

                draft.new = draft.new.filter(term => {
                    const indexNew = updatedTerms.find(term1 => term.id === term1.id);
                    return indexNew === undefined;
                });

                updatedTerms.forEach((term: Term) => {
                    if (term.enabled === null) {
                        draft.new.push(term);
                    } else if (term.enabled) {
                        draft.active.push(term);
                    } else {
                        draft.inactive.push(term);
                    }
                });
            },
        }
    }
);

const loadTerms: ActionCreatorWithoutPayload = createAction('terms/loadTerms');
const forceImportTerms: ActionCreatorWithPayload<ForceLoadedTermsRequest> = createAction('terms/forceImportTerms');
const updateTerms: ActionCreatorWithPayload<Term[]> = createAction('terms/updateTerms');

export const actions = {
    ...slice.actions,
    loadTerms,
    forceImportTerms,
    updateTerms
};

export default slice.reducer;
